import React from 'react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';
const CookiePolicy: React.FC = () => {
  useDocumentTitle('Cognidaily | Cookie Policy');
  const lastUpdated = 'January 2025';

  return (
    <main>
      <SEOHead
        title="Cookie Policy"
        description="Learn about how Cognidaily uses cookies and similar tracking technologies to enhance your experience on our platform."
        keywords="cookie policy, cookies, tracking technologies, web storage, privacy, browser settings"
        url="https://cognidaily.io/cookie-policy"
      />

      <section className="legal-section">
        <div className="legal-container">
          <div className="legal-card">
            <h1 className="legal-main-title">Cookie Policy</h1>
            <p className="legal-last-updated">Last Updated: {lastUpdated}</p>

            <div className="legal-intro">
              <p>
                This Cookie Policy explains how Cognidaily uses cookies and similar tracking technologies when you visit our platform. It explains what these technologies are, why we use them, and your rights to control our use of them.
              </p>
            </div>

            <div className="legal-content">
              <section className="legal-content-section">
                <h2>1. What Are Cookies?</h2>
                
                <p>
                  Cookies are small text files that are placed on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
                </p>

                <p>
                  Cookies allow a website to recognize your device and store some information about your preferences or past actions. This helps us provide you with a better experience when you return to our platform.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>2. How We Use Cookies</h2>
                
                <p>
                  We use cookies and similar technologies for several purposes:
                </p>

                <h3>2.1 Essential Cookies</h3>
                <p>
                  These cookies are necessary for the platform to function properly. They enable core functionality such as:
                </p>
                <ul>
                  <li>User authentication and session management</li>
                  <li>Security features and fraud prevention</li>
                  <li>Remembering your login status</li>
                  <li>Maintaining your quiz progress</li>
                  <li>CSRF protection</li>
                </ul>
                <p>
                  These cookies cannot be disabled in our systems as they are essential for the platform to work. You can set your browser to block or alert you about these cookies, but some parts of the platform may not function properly if you do so.
                </p>

                <h3>2.2 Functional Cookies</h3>
                <p>
                  These cookies allow the platform to remember choices you make and provide enhanced, personalized features:
                </p>
                <ul>
                  <li>Remembering your preferences and settings</li>
                  <li>Theme preferences (light/dark mode)</li>
                  <li>Language preferences</li>
                  <li>Display preferences</li>
                </ul>
                <p>
                  These cookies may be set by us or by third-party providers whose services we have added to our pages.
                </p>

                <h3>2.3 Analytics Cookies</h3>
                <p>
                  These cookies help us understand how visitors interact with our platform by collecting and reporting information anonymously:
                </p>
                <ul>
                  <li>Page views and navigation patterns</li>
                  <li>Time spent on pages</li>
                  <li>Features used and interactions</li>
                  <li>Error messages and performance issues</li>
                </ul>
                <p>
                  This information helps us improve the platform's functionality and user experience.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>3. Types of Cookies We Use</h2>
                
                <h3>3.1 Session Cookies</h3>
                <p>
                  Session cookies are temporary cookies that are deleted when you close your browser. We use session cookies to:
                </p>
                <ul>
                  <li>Maintain your login session</li>
                  <li>Track your quiz progress during a session</li>
                  <li>Store temporary preferences</li>
                </ul>

                <h3>3.2 Persistent Cookies</h3>
                <p>
                  Persistent cookies remain on your device for a set period or until you delete them. We use persistent cookies to:
                </p>
                <ul>
                  <li>Remember your login credentials (if you choose "Remember Me")</li>
                  <li>Store your theme and display preferences</li>
                  <li>Maintain your account settings</li>
                </ul>

                <h3>3.3 First-Party Cookies</h3>
                <p>
                  First-party cookies are set directly by Cognidaily. These cookies are used to provide core functionality and improve your experience on our platform.
                </p>

                <h3>3.4 Third-Party Cookies</h3>
                <p>
                  Third-party cookies are set by domains other than Cognidaily. We may use third-party cookies for:
                </p>
                <ul>
                  <li>Analytics services</li>
                  <li>Performance monitoring</li>
                  <li>Error tracking</li>
                </ul>
                <p>
                  These third parties have their own privacy policies and cookie practices.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>4. Other Tracking Technologies</h2>
                
                <p>
                  In addition to cookies, we may use other tracking technologies:
                </p>

                <h3>4.1 Local Storage</h3>
                <p>
                  We use browser local storage to store information such as your preferences and settings. This data remains on your device until you clear your browser's local storage.
                </p>

                <h3>4.2 Session Storage</h3>
                <p>
                  Similar to local storage, session storage is used to store temporary data that is cleared when you close your browser tab.
                </p>

                <h3>4.3 Web Beacons</h3>
                <p>
                  We may use web beacons (small invisible images) to track email open rates and understand how users interact with our communications.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>5. Cookie Duration</h2>
                
                <h3>5.1 Session Cookies</h3>
                <p>
                  Session cookies expire when you close your browser. They are used for temporary functionality during your visit.
                </p>

                <h3>5.2 Persistent Cookies</h3>
                <p>
                  Persistent cookies remain on your device for varying periods:
                </p>
                <ul>
                  <li><strong>Authentication cookies:</strong> Typically expire after 30 days of inactivity, or when you log out</li>
                  <li><strong>Preference cookies:</strong> May remain for up to 1 year</li>
                  <li><strong>Analytics cookies:</strong> Typically expire after 2 years</li>
                </ul>
              </section>

              <section className="legal-content-section">
                <h2>6. Managing Your Cookie Preferences</h2>
                
                <h3>6.1 Browser Settings</h3>
                <p>
                  Most web browsers allow you to control cookies through their settings. You can:
                </p>
                <ul>
                  <li>Block all cookies</li>
                  <li>Block third-party cookies only</li>
                  <li>Delete existing cookies</li>
                  <li>Set your browser to notify you before cookies are placed</li>
                </ul>

                <h3>6.2 Browser-Specific Instructions</h3>
                <p>
                  To manage cookies in your browser, please refer to the following links:
                </p>
                <ul>
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                </ul>

                <h3>6.3 Impact of Disabling Cookies</h3>
                <p>
                  Please note that disabling certain cookies may impact your experience on Cognidaily:
                </p>
                <ul>
                  <li>You may need to log in more frequently</li>
                  <li>Some features may not function properly</li>
                  <li>Your preferences may not be saved</li>
                  <li>Quiz progress may not be tracked correctly</li>
                </ul>
              </section>

              <section className="legal-content-section">
                <h2>7. Third-Party Cookies</h2>
                
                <p>
                  Some cookies on our platform are set by third-party services. We work with trusted partners who help us operate and improve Cognidaily. These third parties may set cookies to:
                </p>
                <ul>
                  <li>Analyze platform usage and performance</li>
                  <li>Monitor for errors and technical issues</li>
                  <li>Provide security features</li>
                </ul>

                <p>
                  We do not control these third-party cookies. Please refer to the privacy policies of these third parties for information about their cookie practices.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>8. Updates to This Cookie Policy</h2>
                
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>9. More Information</h2>
                
                <p>
                  For more information about how we handle your personal information, please see our <a href="/privacy-policy">Privacy Policy</a>.
                </p>

                <p>
                  If you have questions about our use of cookies, please contact us at:
                </p>
                <p>
                  <strong>Email:</strong> <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CookiePolicy;

